/**
 * Header - App header with branding, theme toggle, and language switch
 */

import { Sun, Moon, X } from 'lucide-react'
import type { Locale } from '../i18n'
import { t } from '../i18n'
import xclawCat from '../assets/xclaw-cat.svg'

interface HeaderProps {
  hasSession: boolean
  sessionTitle?: string
  isDark: boolean
  locale: Locale
  onToggleTheme: () => void
  onToggleLocale: () => void
  onClear: () => void
}

export function Header({ hasSession, sessionTitle, isDark, locale, onToggleTheme, onToggleLocale, onClear }: HeaderProps) {
  return (
    <header className="shrink-0 grid grid-cols-[auto_1fr_auto] items-center px-4 py-3">
      <a
        href="/"
        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        title="XClaw"
        onClick={(e) => { e.preventDefault(); window.location.href = '/' }}
      >
        <img src={xclawCat} alt="XClaw" className="w-7 h-7" />
        <span className="text-sm font-semibold tracking-[0.18em] text-foreground/80">XCLAW</span>
      </a>

      <div className="flex justify-center">
        {sessionTitle && (
          <span className="text-sm font-semibold text-foreground truncate max-w-md">
            {sessionTitle}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {hasSession && (
          <button
            onClick={onClear}
            className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors"
            title={t(locale, 'clearSession')}
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={onToggleLocale}
          className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors text-xs font-medium min-w-[28px]"
          title={t(locale, 'langSwitch')}
        >
          {t(locale, 'langSwitch')}
        </button>

        <button
          onClick={onToggleTheme}
          className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors"
          title={isDark ? t(locale, 'switchToLight') : t(locale, 'switchToDark')}
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </div>
    </header>
  )
}
